/**
 * The inbox and its pushes (docs/design/social-plan.md 3, "Inbox" and "Notifications") -- pure
 * rules, no I/O. The edge stores notifications and decides per recipient whether one becomes a push;
 * the PWA reads the same kinds and deep links.
 */

/** Received kinds ask something of the person; update kinds only tell them what happened. */
export const RECEIVED_KINDS = [
  "friend_request",
  "group_invite",
  "group_join_request",
  "session_invite",
] as const;
/** What happens to a session you are in: reminders from its alarm, and a change of plan. */
export const SESSION_UPDATE_KINDS = [
  "session_reminder",
  "session_starting",
  "session_moved",
  "session_cancelled",
] as const;
/** A sharing member put a wine on the group's shopping list: news in the inbox, and a push for
 * those who switched the "activity" category on. It names no wine, only the member and the group. */
export const SHOP_UPDATE_KINDS = ["group_shop_tick"] as const;
export const UPDATE_KINDS = [
  "friend_accepted",
  "group_invite_accepted",
  "group_join_approved",
  "card_reaction",
  ...SESSION_UPDATE_KINDS,
  ...SHOP_UPDATE_KINDS,
] as const;
export const NOTIFICATION_KINDS = [...RECEIVED_KINDS, ...UPDATE_KINDS] as const;
/** Opt-in "activity" pushes. They never land in the inbox: the feed and the live status are the
 * one place to read what friends are doing, so a push is only a nudge towards them. */
export const ACTIVITY_PUSH_KINDS = ["activity_tasting_now"] as const;

export type ReceivedKind = (typeof RECEIVED_KINDS)[number];
export type ActivityPushKind = (typeof ACTIVITY_PUSH_KINDS)[number];
export type UpdateKind = (typeof UPDATE_KINDS)[number];
export type SessionUpdateKind = (typeof SESSION_UPDATE_KINDS)[number];
export type ShopUpdateKind = (typeof SHOP_UPDATE_KINDS)[number];
/** The kinds that can also become a push; the rest only ever reach the inbox. */
export type PushKind = ReceivedKind | SessionUpdateKind | ShopUpdateKind | ActivityPushKind;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const PUSH_CATEGORIES = ["invites", "sessions", "activity"] as const;
export type PushCategory = (typeof PUSH_CATEGORIES)[number];
export type PushPrefs = Readonly<Record<PushCategory, boolean>>;

/** Invites are what someone waits on; everything else is opt-in, so proef never nags. */
export const DEFAULT_PUSH_PREFS: PushPrefs = { invites: true, sessions: false, activity: false };

export interface QuietHours {
  readonly start: string;
  readonly end: string;
}
export const DEFAULT_QUIET_HOURS: QuietHours = { start: "22:00", end: "09:00" };
/** Used until a device tells the edge its own zone; proef's people are mostly Dutch. */
export const DEFAULT_TIME_ZONE = "Europe/Amsterdam";

/** Pushes per person per UTC day; anything past it waits in the inbox. */
export const PUSHES_PER_DAY = 5;
/** Updates are read-only history, so a month is enough. */
export const NOTIFICATION_RETENTION_DAYS = 30;
/** How far back a group's log goes. */
export const GROUP_EVENT_RETENTION_DAYS = 90;
/** A "tasting now" line is presence, not history: the plan keeps no presence history, so it goes
 * after a week rather than with the rest of the log. */
export const PRESENCE_LINE_RETENTION_DAYS = 7;

export const GROUP_EVENT_KINDS = [
  "group_created",
  "member_joined",
  "member_left",
  "member_removed",
  "role_changed",
  "goal_reached",
  "session_planned",
  "rsvp",
  "session_started",
  "session_result",
  "presence_live",
] as const;
export type GroupEventKind = (typeof GROUP_EVENT_KINDS)[number];
/** The log lines about a session. A client that predates them cannot parse them, so the edge only
 * sends them to one that asks (`?sessions=1`). */
export const SESSION_EVENT_KINDS = [
  "session_planned",
  "rsvp",
  "session_started",
  "session_result",
  "presence_live",
] as const satisfies readonly GroupEventKind[];

/** Accepts and outcomes go to the inbox only; a push is for what needs an answer, or for a session
 * about to start or changed, which is its own opt-in switch. A tick on a group's wine list is an
 * "activity" push, off until the person turns that category on. */
export function pushCategoryOf(kind: NotificationKind | ActivityPushKind): PushCategory | null {
  if ((RECEIVED_KINDS as readonly string[]).includes(kind)) return "invites";
  if ((ACTIVITY_PUSH_KINDS as readonly string[]).includes(kind)) return "activity";
  if (isShopUpdateKind(kind)) return "activity";
  return (SESSION_UPDATE_KINDS as readonly string[]).includes(kind) ? "sessions" : null;
}

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isTimeOfDay(value: string): boolean {
  return TIME_OF_DAY.test(value);
}

function minutesOf(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

function localMinutes(timeZone: string, now: Date): number {
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return localMinutes(DEFAULT_TIME_ZONE, now);
  }
  const parts = format.formatToParts(now);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return hour * 60 + minute;
}

/** Whether `now` falls inside the quiet window on the person's own clock. The window may wrap past
 * midnight; an equal start and end means no quiet hours at all. */
export function isQuietTime(quiet: QuietHours | null, timeZone: string, now: Date): boolean {
  if (quiet === null || !isTimeOfDay(quiet.start) || !isTimeOfDay(quiet.end)) return false;
  const start = minutesOf(quiet.start);
  const end = minutesOf(quiet.end);
  if (start === end) return false;
  const at = localMinutes(timeZone, now);
  return start < end ? at >= start && at < end : at >= start || at < end;
}

export interface PushContext {
  readonly kind: NotificationKind | ActivityPushKind;
  readonly prefs: PushPrefs;
  readonly quiet: QuietHours | null;
  readonly timeZone: string;
  /** The recipient muted the actor, or the group the notification is about. */
  readonly muted: boolean;
  readonly sentToday: number;
  /** The actor shares a friend or a group with the recipient. */
  readonly connected: boolean;
}

export type PushVerdict =
  | "push"
  | "no_category"
  | "category_off"
  | "muted"
  | "stranger"
  | "quiet"
  | "capped";

/** Whether a notification also becomes a push. Anything but "push" still lands in the inbox; a
 * dropped push is never queued for later, so quiet hours stay quiet in the morning too. */
export function pushVerdict(context: PushContext, now: Date): PushVerdict {
  const category = pushCategoryOf(context.kind);
  if (category === null) return "no_category";
  if (!context.prefs[category]) return "category_off";
  if (context.muted) return "muted";
  // A stranger's request is the one push someone could use to reach a person they never met.
  if (context.kind === "friend_request" && !context.connected) return "stranger";
  if (isQuietTime(context.quiet, context.timeZone, now)) return "quiet";
  if (context.sentToday >= PUSHES_PER_DAY) return "capped";
  return "push";
}

export interface NotificationSubject {
  readonly actorId?: string | undefined;
  readonly groupId?: string | undefined;
  readonly sessionId?: string | undefined;
}

/** The stable key of a received item, shared by deep links and the inbox rows they point at. A
 * session news item has no row to point at; its key only has to tell one kind and session apart. */
export function inboxItemKey(kind: PushKind, subject: NotificationSubject): string {
  switch (kind) {
    case "friend_request":
      return `friend_request:${subject.actorId ?? ""}`;
    case "group_invite":
      return `group_invite:${subject.groupId ?? ""}`;
    case "group_join_request":
      return `group_join_request:${subject.groupId ?? ""}:${subject.actorId ?? ""}`;
    case "group_shop_tick":
      return `group_shop_tick:${subject.groupId ?? ""}`;
    default:
      return `${kind}:${subject.sessionId ?? ""}`;
  }
}

export function isReceivedKind(kind: string): kind is ReceivedKind {
  return (RECEIVED_KINDS as readonly string[]).includes(kind);
}

export function isSessionUpdateKind(kind: string): kind is SessionUpdateKind {
  return (SESSION_UPDATE_KINDS as readonly string[]).includes(kind);
}

export function isShopUpdateKind(kind: string): kind is ShopUpdateKind {
  return (SHOP_UPDATE_KINDS as readonly string[]).includes(kind);
}

export function isActivityPushKind(kind: string): kind is ActivityPushKind {
  return (ACTIVITY_PUSH_KINDS as readonly string[]).includes(kind);
}

export function isPushKind(kind: string): kind is PushKind {
  return (
    isReceivedKind(kind) ||
    isSessionUpdateKind(kind) ||
    isShopUpdateKind(kind) ||
    isActivityPushKind(kind)
  );
}

/** The app route a notification opens: the inbox row for a question, the place for an update. */
export function notificationRoute(
  kind: NotificationKind | ActivityPushKind,
  subject: NotificationSubject,
): string {
  if (isReceivedKind(kind)) {
    const params = new URLSearchParams({ tab: "received", item: inboxItemKey(kind, subject) });
    return `/samen?${params.toString()}`;
  }
  if (kind === "friend_accepted" || kind === "activity_tasting_now") return "/friends";
  if (kind === "card_reaction") return "/samen/activity";
  if (kind === "group_shop_tick")
    return `/groups/${encodeURIComponent(subject.groupId ?? "")}/shop`;
  if (isSessionUpdateKind(kind)) {
    const session = `/session/${encodeURIComponent(subject.sessionId ?? "")}`;
    // The screen decides: a participant of a running tasting goes on into it, anyone else lands on
    // the session. The edge cannot know which, and a flight's code does not belong in a payload.
    return kind === "session_reminder" || kind === "session_starting"
      ? `${session}?continue=1`
      : session;
  }
  return `/groups/${encodeURIComponent(subject.groupId ?? "")}`;
}
